/**
 * Bundle Report Page
 *
 * Pharmacy cost still owed on multi-fill programs.
 *
 * A bundle's fills are written as real orders at purchase time, so this counts
 * rows rather than projecting a schedule. Owed (scheduled, not yet at a
 * pharmacy), in flight (being dispensed now) and dispensed (done, with realized
 * COGS) are three different questions and are never summed into one figure.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { format } from 'date-fns'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { billingReportsAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { Card, CardContent, CardHeader, CardTitle, Button } from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, Boxes, CalendarClock, Wallet, Info, TrendingUp,
} from 'lucide-react'

const HORIZONS = [
  { days: 90, label: '90 days' },
  { days: 180, label: '6 months' },
  { days: 365, label: '12 months' },
]

function formatCurrency(cents, compact = false) {
  if (cents === null || cents === undefined) return '—'
  const dollars = cents / 100
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    ...(compact && Math.abs(dollars) >= 1000
      ? { notation: 'compact', maximumFractionDigits: 1 }
      : { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
  }).format(dollars)
}

function formatNumber(value) {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('en-US').format(value)
}

function formatMonth(key) {
  const [year, month] = key.split('-')
  return format(new Date(Number(year), Number(month) - 1, 1), 'MMM yyyy')
}

/**
 * Drift between what a fill is forecast to cost and what one has really cost.
 *
 * Returned as null rather than zero when either side is unknown: a program with
 * no shipped fills has no drift, which is not the same as no difference.
 */
function drift(forecast, realized) {
  if (!forecast || !realized) return null
  return (realized - forecast) / forecast
}

function StatCard({ title, value, subtitle, icon: Icon, loading }) {
  return (
    <Card>
      <CardContent className="p-5">
        {loading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-1/2" />
            <div className="h-8 bg-muted rounded w-3/4" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">{title}</span>
              {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
            </div>
            <div className="text-2xl font-bold tracking-tight">{value}</div>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
      <p className="font-medium text-sm mb-1">{label}</p>
      <p className="text-xs text-muted-foreground">
        {formatNumber(payload[0].payload.fills)} fills · {formatCurrency(payload[0].payload.cost)}
      </p>
    </div>
  )
}

export default function BundleReportPage() {
  const { clinics, currentClinicId } = useAuthStore()
  const [horizonDays, setHorizonDays] = useState(180)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [unavailable, setUnavailable] = useState(null)

  const clinicName = useMemo(
    () => clinics?.find(c => c.id === currentClinicId)?.name || currentClinicId,
    [clinics, currentClinicId]
  )

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await billingReportsAPI.getBundles({ horizonDays })
      if (response.data.success) {
        setData(response.data.data)
        setUnavailable(null)
      }
    } catch (err) {
      console.error('[BUNDLES] Error:', err)
      const apiError = err.response?.data?.error
      if (apiError?.code === 'NO_MANIFOLD_TENANT' || apiError?.code === 'CLINIC_SELECTION_REQUIRED') {
        setUnavailable(apiError.message)
        setData(null)
        return
      }
      setError(apiError?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [horizonDays])

  useEffect(() => { fetchData() }, [fetchData, currentClinicId])

  const summary = data?.summary
  const chartData = (data?.byMonth || []).map(m => ({ ...m, label: formatMonth(m.month) }))
  const programs = data?.byProgram?.filter(p => p.owed_count > 0) || []
  const avgRealized = summary?.dispensed?.realizedPriced > 0
    ? Math.round(summary.dispensed.realizedCogs / summary.dispensed.realizedPriced)
    : null

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Bundle Report</h1>
          <p className="text-muted-foreground">
            Pharmacy cost owed on unplaced fills
            {clinicName && (
              <>
                {' for '}
                <span className="font-medium text-foreground">{clinicName}</span>
              </>
            )}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex rounded-lg border border-input overflow-hidden">
            {HORIZONS.map(h => (
              <button
                key={h.days}
                onClick={() => setHorizonDays(h.days)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  horizonDays === h.days
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:bg-muted'
                }`}
              >
                {h.label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {unavailable && (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Report Unavailable</h3>
            <p className="text-muted-foreground max-w-lg mx-auto">{unavailable}</p>
          </CardContent>
        </Card>
      )}

      {error && !unavailable && (
        <Card className="border-destructive">
          <CardContent className="py-6 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
            <div>
              <p className="font-medium text-destructive">Error loading report</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} className="ml-auto">Retry</Button>
          </CardContent>
        </Card>
      )}

      {!unavailable && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Fills owed"
              value={formatNumber(summary?.owed?.count)}
              subtitle={summary ? `across ${formatNumber(summary.bundles)} bundles` : null}
              icon={Boxes}
              loading={loading}
            />
            <StatCard
              title={`Cost owed, next ${horizonDays} days`}
              value={formatCurrency(summary?.owed?.costInHorizon)}
              subtitle={summary ? `${formatCurrency(summary.owed.cost)} owed in total` : null}
              icon={Wallet}
              loading={loading}
            />
            <StatCard
              title="In flight"
              value={formatNumber(summary?.inFlight?.count)}
              subtitle={summary ? `${formatCurrency(summary.inFlight.cost)} being dispensed now` : null}
              icon={CalendarClock}
              loading={loading}
            />
            <StatCard
              title="Realized cost per fill"
              value={formatCurrency(avgRealized)}
              subtitle={summary?.dispensed?.realizedPriced > 0
                ? `over ${formatNumber(summary.dispensed.realizedPriced)} completed fills`
                : 'Not recorded for this clinic'}
              icon={TrendingUp}
              loading={loading}
            />
          </div>

          {summary?.unpricedOwed > 0 && (
            <Card className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30">
              <CardContent className="py-3 flex items-start gap-2 text-sm">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                <span>
                  {formatNumber(summary.unpricedOwed)} owed fills are on a program with no pharmacy price,
                  so they count toward the fill total but contribute nothing to the cost. The real figure
                  is higher than shown.
                </span>
              </CardContent>
            </Card>
          )}

          {summary?.overdueOwed > 0 && (
            <Card className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30">
              <CardContent className="py-3 flex items-start gap-2 text-sm">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                <span>
                  {formatNumber(summary.overdueOwed)} owed fills are past their refill date. They are
                  counted in the totals but kept out of the chart, which only shows fills still ahead.
                </span>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CalendarClock className="h-4 w-4" />
                Cost owed by month
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-64 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : chartData.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
                  No owed fills fall due inside this horizon.
                </div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={v => formatCurrency(v, true)}
                      />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="cost" name="Cost owed" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">By program</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-48 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : programs.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">
                  No bundle programs with owed fills.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="py-2 pr-4 font-medium">Program</th>
                        <th className="py-2 pr-4 font-medium text-right">Fills owed</th>
                        <th className="py-2 pr-4 font-medium text-right">In flight</th>
                        <th className="py-2 pr-4 font-medium text-right">Dispensed</th>
                        <th className="py-2 pr-4 font-medium text-right">Cancelled</th>
                        <th className="py-2 pr-4 font-medium text-right">Cost / fill</th>
                        <th className="py-2 pr-4 font-medium text-right">Realized / fill</th>
                        <th className="py-2 pr-4 font-medium text-right">Drift</th>
                        <th className="py-2 font-medium text-right">Cost owed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {programs.map(program => {
                        const d = drift(program.avg_owed_cost, program.avg_realized_cost)
                        return (
                          <tr key={program.entity_id} className="border-b last:border-0 hover:bg-muted/40">
                            <td className="py-2 pr-4">
                              <div className="font-medium">{program.program_name}</div>
                              <div className="text-xs text-muted-foreground font-mono">{program.entity_id}</div>
                            </td>
                            <td className="py-2 pr-4 text-right tabular-nums">{formatNumber(program.owed_count)}</td>
                            <td className="py-2 pr-4 text-right tabular-nums text-muted-foreground">{formatNumber(program.in_flight_count)}</td>
                            <td className="py-2 pr-4 text-right tabular-nums text-muted-foreground">{formatNumber(program.dispensed_count)}</td>
                            <td className="py-2 pr-4 text-right tabular-nums text-muted-foreground">{formatNumber(program.cancelled_count)}</td>
                            <td className="py-2 pr-4 text-right tabular-nums">{formatCurrency(program.avg_owed_cost)}</td>
                            <td className="py-2 pr-4 text-right tabular-nums">{formatCurrency(program.avg_realized_cost)}</td>
                            <td className={`py-2 pr-4 text-right tabular-nums ${
                              d === null ? 'text-muted-foreground'
                                : d > 0.05 ? 'text-destructive'
                                : d < -0.05 ? 'text-green-600 dark:text-green-400'
                                : ''
                            }`}>
                              {d === null ? '—' : `${d > 0 ? '+' : ''}${(d * 100).toFixed(0)}%`}
                            </td>
                            <td className="py-2 text-right tabular-nums font-medium">
                              {formatCurrency(program.owed_cost)}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {data?.assumptions && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Info className="h-4 w-4" />
                  How these numbers were produced
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5 text-xs text-muted-foreground">
                  <li>{data.assumptions.definition}</li>
                  <li>{data.assumptions.owedBasis}</li>
                  <li>{data.assumptions.dueDates}</li>
                  <li>{data.assumptions.realizedBasis}</li>
                  <li>{data.assumptions.lapsed}</li>
                  <li className="text-foreground">{data.assumptions.cancelled}</li>
                  {data.truncated && (
                    <li className="text-amber-600 dark:text-amber-400">
                      The order read hit its cap, so some plans are missing from these totals.
                    </li>
                  )}
                </ul>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
